import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  CalendarRange,
  ChevronDown,
  ChevronRight,
  Gauge,
  RefreshCw,
  Target,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Tier1PortfolioPanel from '../components/Tier1PortfolioPanel';
import Tier6OptionsPanel from '../components/Tier6OptionsPanel';
import AccountPortfolioOverviewCard from '../components/portfolio/AccountPortfolioOverviewCard';
import PortfolioTierEmptyState from '../components/portfolio/PortfolioTierEmptyState';
import { findTier1MacroTier, findTier6OptionsTier } from '../constants/tierPresets';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import TierChip from '../components/portfolio/TierChip';
import TierSpectrumRail from '../components/portfolio/TierSpectrumRail';
import TierPerformanceOverTimeChart from '../components/portfolio/TierPerformanceOverTimeChart';
import { useTierPortfolio } from '../hooks/useTierPortfolio';
import { groupPositionsByTier } from '../tiers/resolveTierContext';
import { TIER_CATALOG } from '../tiers/tierCatalog';
import { AccountTierContextResponse } from '../types/tierContext';
import { ALL_RISK_TIER_NUMBERS } from '../tiers/tierCatalog';
import { TierBudgetSnapshot } from '../types/tier1Portfolio';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import {
  Account,
  AccountPerformanceReviewResponse,
  AccountPosition,
  DailySuggestionCohortRow,
  IntradayCheckpointLabel,
  PerformanceReviewTrustAssessment,
  PositionIntradaySnapshot,
  ReviewTradeRow,
  SuggestionIntradayBatchSummary,
} from '../types';

type AccountTab =
  | 'overview'
  | 'positions'
  | 'performanceReview'
  | 'tier1Portfolio'
  | 'tier6Portfolio';
type ReviewDatePreset = '7d' | '14d' | '30d' | '90d' | 'all';

const BASE_TABS: Array<{ key: AccountTab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'positions', label: 'Positions' },
  { key: 'performanceReview', label: 'Performance Review' },
];

const DATE_PRESETS: Array<{ key: ReviewDatePreset; label: string }> = [
  { key: '7d', label: '7D' },
  { key: '14d', label: '14D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: 'all', label: 'All' },
];

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2,
  }).format(value);
};

const formatNumber = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }

  return new Intl.NumberFormat('en-US').format(value);
};

const formatPercent = (value?: number | null, digits: number = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }

  return `${value.toFixed(digits)}%`;
};

const formatSignedPercent = (value?: number | null, digits: number = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }

  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toFixed(digits)}%`;
};

const formatRatioPercent = (value?: number | null, digits: number = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }

  return `${(value * 100).toFixed(digits)}%`;
};

const formatSignedRatioPercent = (
  value?: number | null,
  digits: number = 1,
) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }

  const percent = value * 100;
  const prefix = percent > 0 ? '+' : '';
  return `${prefix}${percent.toFixed(digits)}%`;
};

const formatConfidence = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }

  return `${Math.round(value)}%`;
};

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Intl.DateTimeFormat(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleDateString();
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'N/A';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString();
};

const formatBucketLabel = (value?: string | null) => {
  if (!value) return 'N/A';

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
    }).format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const getReviewQuery = (preset: ReviewDatePreset) => {
  const now = new Date();

  if (preset === 'all') {
    return {
      groupBy: 'month' as const,
      includeRows: true,
      limit: 50,
    };
  }

  const days =
    preset === '7d' ? 7 : preset === '14d' ? 14 : preset === '30d' ? 30 : 90;
  const from = new Date(now);
  from.setDate(now.getDate() - days);

  return {
    from: from.toISOString(),
    to: now.toISOString(),
    groupBy: days > 30 ? ('week' as const) : ('day' as const),
    includeRows: true,
    limit: 50,
  };
};

const getTrustTone = (
  label?: AccountPerformanceReviewResponse['summary']['trustLabel'],
) => {
  switch (label) {
    case 'strong':
      return 'bg-green-100 text-green-800';
    case 'improving':
      return 'bg-blue-100 text-blue-800';
    case 'mixed':
      return 'bg-yellow-100 text-yellow-800';
    case 'weak':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatTrustLabel = (
  label?: PerformanceReviewTrustAssessment['label'],
) => {
  if (!label) {
    return 'trust unavailable';
  }

  return label.replace(/_/g, ' ');
};

const getTrustAssessment = (
  summary?: AccountPerformanceReviewResponse['summary'],
): PerformanceReviewTrustAssessment | null => {
  if (summary?.trust) {
    return summary.trust;
  }

  if (!summary?.trustLabel) {
    return null;
  }

  return {
    label: summary.trustLabel,
    headline: `Decision readiness is ${formatTrustLabel(summary.trustLabel)}.`,
    rationale:
      'This backend returned a trust label without additional operator guidance.',
  };
};

const getPositionStatusTone = (status: AccountPosition['status']) => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'CLOSING':
      return 'bg-amber-100 text-amber-900';
    case 'RECOMMENDED':
    case 'APPROVED':
    case 'ENTRY_PENDING':
    case 'PENDING_MARKET_OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'CLOSED':
      return 'bg-gray-100 text-gray-700';
    case 'REJECTED':
    case 'CANCELLED':
    case 'ORDERS_REJECTED':
    case 'EXPIRED_TIMEOUT':
    case 'CANCELLED_PRICE_MOVEMENT':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getTimeframeVerdictTone = (label?: 'good' | 'neutral' | 'bad') => {
  switch (label) {
    case 'good':
      return 'bg-green-100 text-green-800';
    case 'bad':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

const getTimeframeStatusTone = (
  status?: 'ok' | 'low_sample' | 'insufficient_data',
) => {
  switch (status) {
    case 'ok':
      return 'bg-green-100 text-green-800';
    case 'low_sample':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatCohortLabel = (cohort?: string | null) => {
  if (!cohort) return 'N/A';
  return cohort.replace(/_/g, ' ');
};

const formatStatusLabel = (value?: string | null) => {
  if (!value) return 'N/A';
  return value.replace(/_/g, ' ');
};

const EmptyState: React.FC<{
  title: string;
  description: string;
  action?: React.ReactNode;
}> = ({ title, description, action }) => (
  <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
    <p className="text-sm font-semibold text-gray-900">{title}</p>
    <p className="mt-2 text-sm text-gray-500">{description}</p>
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

const MetricRow: React.FC<{
  label: string;
  value: string;
  valueClassName?: string;
}> = ({ label, value, valueClassName = 'text-gray-900' }) => (
  <div className="flex items-center justify-between gap-4 border-b border-gray-100 py-2 last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className={`text-sm font-medium ${valueClassName}`}>{value}</span>
  </div>
);

const SummaryCard: React.FC<{
  label: string;
  value: string;
  helper?: string;
  icon: React.ReactNode;
}> = ({ label, value, helper, icon }) => (
  <Card>
    <CardContent className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
        {helper ? <p className="mt-1 text-xs text-gray-500">{helper}</p> : null}
      </div>
      <div className="rounded-lg bg-gray-100 p-3 text-gray-700">{icon}</div>
    </CardContent>
  </Card>
);

const getCohortWindow = (row: DailySuggestionCohortRow, label: string) =>
  row.windows.find((window) => window.label === label);

const getDrilldownReturn = (
  row: DailySuggestionCohortRow['recommendations'][number],
  label: string,
) => row.returns.find((entry) => entry.label === label);

const getReturnTone = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'text-gray-400';
  }

  if (value > 0) return 'text-green-600';
  if (value < 0) return 'text-red-600';
  return 'text-gray-700';
};

const formatPricingSummary = (row: DailySuggestionCohortRow) => {
  const pricing = row.pricing;
  if (!pricing) return 'N/A';

  const parts = [
    pricing.underlyingClose > 0 ? `${pricing.underlyingClose} ticker` : null,
    pricing.realOptionContract > 0
      ? `${pricing.realOptionContract} option`
      : null,
    pricing.modeledProjection > 0
      ? `${pricing.modeledProjection} modeled`
      : null,
    pricing.unavailable > 0 ? `${pricing.unavailable} pending` : null,
  ].filter(Boolean);

  return parts.length ? parts.join(' / ') : 'N/A';
};

const DailySuggestionCohortTable: React.FC<{
  cohorts: NonNullable<
    AccountPerformanceReviewResponse['dailySuggestionCohorts']
  >;
}> = ({ cohorts }) => {
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  if (!cohorts.rows.length) {
    return (
      <EmptyState
        title="No daily suggestion cohorts yet"
        description="No materialized suggestion outcomes were found for this account in the selected window."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-10 px-4 py-3" />
            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Generated
            </th>
            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Suggestions
            </th>
            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Selected
            </th>
            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Avg Confidence
            </th>
            {cohorts.windowLabels.map((label) => (
              <th
                key={label}
                className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                {label} Avg
              </th>
            ))}
            <th className="min-w-[180px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Symbols
            </th>
            <th className="min-w-[180px] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Pricing
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {cohorts.rows.map((row) => {
            const isExpanded = expandedDate === row.date;
            return (
              <React.Fragment key={row.date}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedDate(isExpanded ? null : row.date)
                      }
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                      aria-label={
                        isExpanded
                          ? 'Collapse daily recommendations'
                          : 'Expand daily recommendations'
                      }
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                    {formatDate(row.date)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                    {formatNumber(row.generatedCount)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                    {formatNumber(row.selectedCount)}
                    <span className="text-gray-400">
                      {' / '}
                      {formatNumber(row.skippedCount)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                    {formatConfidence(row.avgConfidence)}
                  </td>
                  {cohorts.windowLabels.map((label) => {
                    const window = getCohortWindow(row, label);
                    const value = window?.avgReturnPct;
                    return (
                      <td
                        key={label}
                        className={`whitespace-nowrap px-4 py-3 text-sm font-medium ${getReturnTone(
                          value,
                        )}`}
                      >
                        {formatSignedPercent(value)}
                        {window?.eligibleCount ? (
                          <span className="ml-1 text-xs font-normal text-gray-400">
                            n={window.eligibleCount}
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {row.symbols.length ? row.symbols.join(', ') : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatPricingSummary(row)}
                  </td>
                </tr>
                {isExpanded ? (
                  <tr>
                    <td
                      colSpan={cohorts.windowLabels.length + 7}
                      className="bg-gray-50 px-4 py-4"
                    >
                      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Symbol
                              </th>
                              <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Direction
                              </th>
                              <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Confidence
                              </th>
                              <th className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Selected
                              </th>
                              {cohorts.windowLabels.map((label) => (
                                <th
                                  key={label}
                                  className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                                >
                                  {label}
                                </th>
                              ))}
                              <th className="min-w-[190px] px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Pricing
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {row.recommendations.map((recommendation) => (
                              <tr
                                key={
                                  recommendation.recommendationId ||
                                  `${recommendation.symbol}-${recommendation.generatedAt}`
                                }
                              >
                                <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900">
                                  {recommendation.symbol}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {recommendation.tradeDirection || 'N/A'}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {formatConfidence(recommendation.confidence)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {recommendation.selected ? 'Yes' : 'No'}
                                </td>
                                {cohorts.windowLabels.map((label) => {
                                  const value = getDrilldownReturn(
                                    recommendation,
                                    label,
                                  )?.returnPct;
                                  return (
                                    <td
                                      key={label}
                                      className={`whitespace-nowrap px-3 py-2 text-sm font-medium ${getReturnTone(
                                        value,
                                      )}`}
                                    >
                                      {formatSignedPercent(value)}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 text-sm text-gray-700">
                                  {recommendation.pricingLabel}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TradeHighlight: React.FC<{
  title: string;
  trade?: ReviewTradeRow | null;
}> = ({ title, trade }) => {
  if (!trade) {
    return (
      <div className="rounded-lg border border-gray-200 p-4">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="mt-2 text-sm text-gray-500">No reviewed trade yet.</p>
      </div>
    );
  }

  const returnPct = trade.closeReturnPct ?? trade.liveReturnPct;

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <div className="mt-3 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Symbol</span>
          <span className="font-medium text-gray-900">{trade.symbol}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Date</span>
          <span className="font-medium text-gray-900">
            {formatDate(trade.date)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Return</span>
          <span
            className={`font-medium ${
              (returnPct ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {formatPercent(returnPct)}
          </span>
        </div>
      </div>
    </div>
  );
};

const CHECKPOINT_LABELS: IntradayCheckpointLabel[] = [
  '10:00',
  '12:00',
  '14:00',
  '15:45',
];

const PositionIntradayTimeline: React.FC<{
  snapshots: PositionIntradaySnapshot[];
  symbol: string;
}> = ({ snapshots, symbol }) => {
  if (!snapshots.length) {
    return (
      <EmptyState
        title="No intraday snapshots yet"
        description="15-minute window high/low samples are recorded each minute while a position is actively monitored during market hours."
      />
    );
  }

  const chartData = snapshots.map((snapshot) => ({
    time: snapshot.windowLabel || formatDateTime(snapshot.capturedAt),
    legReturnPct: snapshot.legReturnPct,
    legReturnHighPct: snapshot.legReturnHighPct ?? snapshot.legReturnPct,
    legReturnLowPct: snapshot.legReturnLowPct ?? snapshot.legReturnPct,
    totalReturnPct: snapshot.totalReturnPct,
  }));

  const sessionPeak = Math.max(
    ...snapshots.map(
      (snapshot) => snapshot.legReturnHighPct ?? snapshot.legReturnPct,
    ),
  );
  const sessionTrough = Math.min(
    ...snapshots.map(
      (snapshot) => snapshot.legReturnLowPct ?? snapshot.legReturnPct,
    ),
  );

  return (
    <div className="space-y-4">
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" hide />
            <YAxis tickFormatter={(value) => `${value}%`} />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatSignedPercent(value),
                name,
              ]}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="legReturnHighPct"
              name="Window High"
              stroke="#15803d"
              strokeWidth={1.5}
              dot={false}
              strokeDasharray="4 3"
            />
            <Line
              type="monotone"
              dataKey="legReturnPct"
              name="Window Close"
              stroke="#2563eb"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="legReturnLowPct"
              name="Window Low"
              stroke="#dc2626"
              strokeWidth={1.5}
              dot={false}
              strokeDasharray="4 3"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Window
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Close
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                High
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Low
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Underlying
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Samples
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {snapshots.map((snapshot) => (
              <tr key={snapshot._id}>
                <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                  {snapshot.windowLabel || formatDateTime(snapshot.capturedAt)}
                </td>
                <td
                  className={`whitespace-nowrap px-3 py-2 font-medium ${getReturnTone(
                    snapshot.legReturnPct,
                  )}`}
                >
                  {formatSignedPercent(snapshot.legReturnPct)}
                </td>
                <td
                  className={`whitespace-nowrap px-3 py-2 font-medium ${getReturnTone(
                    snapshot.legReturnHighPct,
                  )}`}
                >
                  {formatSignedPercent(snapshot.legReturnHighPct)}
                </td>
                <td
                  className={`whitespace-nowrap px-3 py-2 font-medium ${getReturnTone(
                    snapshot.legReturnLowPct,
                  )}`}
                >
                  {formatSignedPercent(snapshot.legReturnLowPct)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                  {formatCurrency(snapshot.underlyingPrice)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                  {formatNumber(snapshot.sampleCount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500">
        Intraday windows for {symbol}. Session peak{' '}
        {formatSignedPercent(sessionPeak)} · trough{' '}
        {formatSignedPercent(sessionTrough)}
      </p>
    </div>
  );
};

const SuggestionBatchCheckpointTable: React.FC<{
  batches: SuggestionIntradayBatchSummary[];
}> = ({ batches }) => {
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);

  if (!batches.length) {
    return (
      <EmptyState
        title="No intraday checkpoints yet"
        description="Same-day suggestion windows accumulate high/low each minute via account orchestration, then finalize at 10:00, 12:00, 14:00, and 15:45 ET."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-10 px-4 py-3" />
            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Session
            </th>
            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Batch
            </th>
            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Suggestions
            </th>
            {CHECKPOINT_LABELS.map((label) => (
              <th
                key={label}
                className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                {label} Avg
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {batches.map((batch) => {
            const isExpanded = expandedBatchId === batch.batchId;
            const checkpointAverages = CHECKPOINT_LABELS.map((label) => {
              const values = batch.rows
                .map((row) => row.checkpointReturns[label]?.directionalReturnPct)
                .filter(
                  (value): value is number =>
                    typeof value === 'number' && Number.isFinite(value),
                );
              if (!values.length) return null;
              return values.reduce((sum, value) => sum + value, 0) / values.length;
            });
            const checkpointHighs = CHECKPOINT_LABELS.map((label) => {
              const values = batch.rows
                .map(
                  (row) => row.checkpointReturns[label]?.directionalReturnHighPct,
                )
                .filter(
                  (value): value is number =>
                    typeof value === 'number' && Number.isFinite(value),
                );
              if (!values.length) return null;
              return Math.max(...values);
            });

            return (
              <React.Fragment key={`${batch.batchId}-${batch.sessionDate}`}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedBatchId(isExpanded ? null : batch.batchId)
                      }
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                    {formatDate(batch.sessionDate)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {batch.batchId}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                    {formatNumber(batch.recommendationCount)}
                  </td>
                  {checkpointAverages.map((average, index) => (
                    <td
                      key={`${batch.batchId}-${CHECKPOINT_LABELS[index]}`}
                      className={`whitespace-nowrap px-4 py-3 text-sm font-medium ${getReturnTone(
                        average,
                      )}`}
                    >
                      {formatSignedPercent(average)}
                      {checkpointHighs[index] != null ? (
                        <span className="ml-1 text-xs font-normal text-gray-400">
                          H {formatSignedPercent(checkpointHighs[index])}
                        </span>
                      ) : null}
                    </td>
                  ))}
                </tr>
                {isExpanded ? (
                  <tr>
                    <td colSpan={4 + CHECKPOINT_LABELS.length} className="bg-gray-50 px-4 py-4">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead>
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Rank
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Symbol
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Opened
                            </th>
                            {CHECKPOINT_LABELS.map((label) => (
                              <th
                                key={label}
                                className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                              >
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {batch.rows.map((row) => (
                            <tr key={row.recommendationId}>
                              <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                                {row.rank ?? '—'}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">
                                {row.symbol}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                                {row.wasOpened ? 'Yes' : 'No'}
                              </td>
                              {CHECKPOINT_LABELS.map((label) => {
                                const checkpoint = row.checkpointReturns[label];
                                return (
                                  <td
                                    key={`${row.recommendationId}-${label}`}
                                    className="whitespace-nowrap px-3 py-2 text-sm"
                                  >
                                    <div
                                      className={`font-medium ${getReturnTone(
                                        checkpoint?.directionalReturnPct,
                                      )}`}
                                    >
                                      {formatSignedPercent(
                                        checkpoint?.directionalReturnPct,
                                      )}
                                    </div>
                                    {checkpoint ? (
                                      <div className="text-xs text-gray-500">
                                        H{' '}
                                        {formatSignedPercent(
                                          checkpoint.directionalReturnHighPct,
                                        )}{' '}
                                        · L{' '}
                                        {formatSignedPercent(
                                          checkpoint.directionalReturnLowPct,
                                        )}
                                      </div>
                                    ) : null}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AccountDetail: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    setSelectedAccountId,
    tierFilter,
    setTierFilter,
    accountContext,
  } = useTierPortfolio();
  const [localTierContext, setLocalTierContext] =
    useState<AccountTierContextResponse | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [tierBudgets, setTierBudgets] = useState<TierBudgetSnapshot[]>([]);
  const [positions, setPositions] = useState<AccountPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AccountTab>('overview');

  const macroTier1Slot = useMemo(
    () => findTier1MacroTier(account?.riskTiers),
    [account],
  );
  const optionsTier6Slot = useMemo(
    () => findTier6OptionsTier(account?.riskTiers),
    [account],
  );
  const macroTier1 = macroTier1Slot?.enabled ? macroTier1Slot : undefined;
  const optionsTier6 = optionsTier6Slot?.enabled ? optionsTier6Slot : undefined;
  const accountTabs = useMemo(() => {
    const extra: Array<{ key: AccountTab; label: string }> = [];
    if (macroTier1Slot) {
      extra.push({
        key: 'tier1Portfolio',
        label: macroTier1
          ? TIER_CATALOG[1].name
          : `${TIER_CATALOG[1].shortName} · off`,
      });
    }
    if (optionsTier6Slot) {
      extra.push({
        key: 'tier6Portfolio',
        label: optionsTier6
          ? TIER_CATALOG[6].name
          : `${TIER_CATALOG[6].shortName} · off`,
      });
    }
    if (extra.length === 0) return BASE_TABS;
    return [...BASE_TABS, ...extra];
  }, [macroTier1, macroTier1Slot, optionsTier6, optionsTier6Slot]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphansError, setOrphansError] = useState<string | null>(null);
  const [closingOrphanSymbol, setClosingOrphanSymbol] = useState<string | null>(
    null,
  );
  const [orphanActionMessage, setOrphanActionMessage] = useState<string | null>(
    null,
  );
  const [holdingsOrphans, setHoldingsOrphans] = useState<{
    capturedAt?: string;
    schwabPositionCount: number;
    schwabOnly: Array<{
      symbol: string;
      longQuantity: number;
      shortQuantity: number;
      marketValue: number;
      isShort: boolean;
    }>;
    marlinOnly: Array<{
      positionId: string;
      symbol: string;
      status?: string;
      lockedOcc?: string;
      marlinSignedQty: number;
    }>;
    qtyOrSide: Array<{
      positionId: string;
      symbol: string;
      lockedOcc: string;
      marlinSignedQty: number;
      schwabSignedQty: number;
    }>;
  } | null>(null);
  const [reviewPreset, setReviewPreset] = useState<ReviewDatePreset>('14d');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewData, setReviewData] =
    useState<AccountPerformanceReviewResponse | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const state = location.state as {
      initialTab?: AccountTab;
      positionId?: string;
    } | null;
    if (state?.initialTab) {
      setActiveTab(state.initialTab);
    }
    if (state?.positionId) {
      setSelectedPositionId(state.positionId);
    }
  }, [location.state]);

  useEffect(() => {
    if (accountId) {
      setSelectedAccountId(accountId);
    }
  }, [accountId, setSelectedAccountId]);

  useEffect(() => {
    const tierParam = searchParams.get('tier');
    if (!tierParam) return;
    const n = parseInt(tierParam, 10);
    if (n === 1) setActiveTab('tier1Portfolio');
    else if (n === 6) setActiveTab('tier6Portfolio');
    else if (n >= 1 && n <= 6) {
      setActiveTab('positions');
      setTierFilter(n as 1 | 2 | 3 | 4 | 5 | 6);
    }
  }, [searchParams, setTierFilter]);

  const [positionSnapshots, setPositionSnapshots] = useState<
    PositionIntradaySnapshot[]
  >([]);
  const [positionSnapshotsLoading, setPositionSnapshotsLoading] =
    useState(false);
  const [checkpointBatches, setCheckpointBatches] = useState<
    SuggestionIntradayBatchSummary[]
  >([]);
  const [checkpointLoading, setCheckpointLoading] = useState(false);
  const [checkpointError, setCheckpointError] = useState<string | null>(null);

  const loadAccountPage = useCallback(async () => {
    if (!accountId) {
      setPageError('Missing account id.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setPageError(null);

    try {
      const [accountResult, positionsResult] = await Promise.allSettled([
        apiService.getAccount(accountId),
        apiService.getAccountPositions(accountId),
      ]);

      if (accountResult.status === 'rejected') {
        throw accountResult.reason;
      }

      const acct = accountResult.value;
      setAccount(acct);

      const t1 = findTier1MacroTier(acct.riskTiers);
      const t6 = findTier6OptionsTier(acct.riskTiers);
      if ((t1?.enabled || t6?.enabled) && accountId) {
        try {
          setTierBudgets(await apiService.getTierBudgets(accountId));
        } catch {
          setTierBudgets([]);
        }
      } else {
        setTierBudgets([]);
      }

      if (accountId) {
        try {
          const ctx = await apiService.getAccountTierContext(accountId);
          setLocalTierContext({
            ...ctx,
            tiers: ctx.tiers.map((row) => ({
              ...row,
              catalog: TIER_CATALOG[row.riskTier],
            })),
          });
        } catch {
          setLocalTierContext(null);
        }
      }

      if (positionsResult.status === 'fulfilled') {
        setPositions(positionsResult.value.positions || []);
      } else {
        setPositions([]);
      }
    } catch (error: any) {
      setPageError(
        error?.response?.data?.message || 'Failed to load this account page.',
      );
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  const loadPositions = useCallback(async () => {
    if (!accountId) return;

    setPositionsLoading(true);
    try {
      const response = await apiService.getAccountPositions(accountId);
      setPositions(response.positions || []);
    } catch (error) {
      setPositions([]);
    } finally {
      setPositionsLoading(false);
    }
  }, [accountId]);

  const loadHoldingsOrphans = useCallback(async () => {
    if (!accountId) return;

    setOrphansLoading(true);
    setOrphansError(null);
    try {
      const response = await apiService.getHoldingsOrphans(accountId);
      setHoldingsOrphans({
        capturedAt: response.capturedAt,
        schwabPositionCount: response.schwabPositionCount,
        schwabOnly: response.schwabOnly || [],
        marlinOnly: response.marlinOnly || [],
        qtyOrSide: response.qtyOrSide || [],
      });
    } catch (error: any) {
      setHoldingsOrphans(null);
      setOrphansError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to load Schwab orphans.',
      );
    } finally {
      setOrphansLoading(false);
    }
  }, [accountId]);

  const handleCloseOrphan = useCallback(
    async (symbol: string) => {
      if (!accountId) return;
      const confirmed = window.confirm(
        `Close Schwab-only orphan on MARKET?\n\n${symbol}\n\nThis places a broker close without creating a Marlin position.`,
      );
      if (!confirmed) return;

      setClosingOrphanSymbol(symbol);
      setOrphanActionMessage(null);
      try {
        const result = await apiService.closeHoldingsOrphan(accountId, symbol);
        if (result.success) {
          setOrphanActionMessage(
            `Close submitted for ${symbol} (order ${result.schwabOrderId}).`,
          );
        } else {
          setOrphanActionMessage(
            result.errorMessage || result.message || `Close failed for ${symbol}.`,
          );
        }
        await loadHoldingsOrphans();
      } catch (error: any) {
        setOrphanActionMessage(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            `Close failed for ${symbol}.`,
        );
      } finally {
        setClosingOrphanSymbol(null);
      }
    },
    [accountId, loadHoldingsOrphans],
  );

  const loadPerformanceReview = useCallback(async () => {
    if (!accountId) return;

    setReviewLoading(true);
    setReviewError(null);

    try {
      const response = await apiService.getAccountPerformanceReview(
        accountId,
        getReviewQuery(reviewPreset),
      );
      setReviewData(response);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        setReviewError(
          'Performance review is not available on this backend yet. This UI depends on backend PR #109 being deployed locally.',
        );
      } else {
        setReviewError(
          error?.response?.data?.message ||
            'Failed to load performance review data.',
        );
      }
      setReviewData(null);
    } finally {
      setReviewLoading(false);
    }
  }, [accountId, reviewPreset]);

  const loadPositionSnapshots = useCallback(
    async (positionId: string) => {
      if (!accountId) return;

      setPositionSnapshotsLoading(true);
      try {
        const response = await apiService.getPositionIntradaySnapshots(
          accountId,
          positionId,
        );
        setPositionSnapshots(response.snapshots || []);
      } catch {
        setPositionSnapshots([]);
      } finally {
        setPositionSnapshotsLoading(false);
      }
    },
    [accountId],
  );

  const loadCheckpointBatches = useCallback(async () => {
    if (!accountId) return;

    setCheckpointLoading(true);
    setCheckpointError(null);

    try {
      const query = getReviewQuery(reviewPreset);
      const response = await apiService.getSuggestionIntradayCheckpoints(
        accountId,
        query,
      );
      setCheckpointBatches(response.batches || []);
    } catch (error: any) {
      setCheckpointBatches([]);
      setCheckpointError(
        error?.response?.data?.message ||
          'Failed to load suggestion intraday checkpoints.',
      );
    } finally {
      setCheckpointLoading(false);
    }
  }, [accountId, reviewPreset]);

  const refreshPerformanceReview = useCallback(async () => {
    await Promise.all([loadPerformanceReview(), loadCheckpointBatches()]);
  }, [loadPerformanceReview, loadCheckpointBatches]);

  useEffect(() => {
    loadAccountPage();
  }, [loadAccountPage]);

  useEffect(() => {
    if (activeTab === 'positions') {
      void loadHoldingsOrphans();
    }
  }, [activeTab, loadHoldingsOrphans]);

  useEffect(() => {
    if (activeTab === 'performanceReview') {
      refreshPerformanceReview();
    }
  }, [activeTab, refreshPerformanceReview]);

  useEffect(() => {
    if (selectedPositionId) {
      loadPositionSnapshots(selectedPositionId);
    } else {
      setPositionSnapshots([]);
    }
  }, [selectedPositionId, loadPositionSnapshots]);

  const sortedPositions = useMemo(
    () =>
      [...positions].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      ),
    [positions],
  );

  const currentPositions = useMemo(
    () =>
      sortedPositions.filter((position) =>
        ['ACTIVE', 'APPROVED', 'ENTRY_PENDING', 'PENDING_MARKET_OPEN'].includes(
          position.status,
        ),
      ),
    [sortedPositions],
  );

  const positionsByTier = useMemo(
    () => groupPositionsByTier(currentPositions, account ?? undefined),
    [currentPositions, account],
  );

  const tierContextForRail =
    localTierContext?.accountId === accountId
      ? localTierContext
      : accountContext?.accountId === accountId
        ? accountContext
        : null;

  const cumulativePnlData = useMemo(
    () =>
      (reviewData?.timeSeries?.cumulativePnl || []).map((point) => ({
        bucket: point.bucket,
        label: formatBucketLabel(point.bucket),
        selected:
          point.selectedCumulativePnlPerContract === null
            ? undefined
            : point.selectedCumulativePnlPerContract,
        baseline:
          point.baselineCumulativePnlPerContract === null
            ? undefined
            : point.baselineCumulativePnlPerContract,
      })),
    [reviewData],
  );

  const thesisTrendData = useMemo(
    () =>
      (reviewData?.timeSeries?.thesisAccuracy || []).map((point) => ({
        bucket: point.bucket,
        label: formatBucketLabel(point.bucket),
        accuracy:
          point.accuracy === null || point.accuracy === undefined
            ? undefined
            : Number((point.accuracy * 100).toFixed(1)),
        count: point.count,
      })),
    [reviewData],
  );

  const confidenceBucketData = useMemo(
    () =>
      (reviewData?.confidenceCalibration?.buckets || []).map((bucket) => ({
        label: bucket.label,
        sampleCount: bucket.sampleCount,
        selectedCount: bucket.selectedCount,
        winRate:
          bucket.winRate === null || bucket.winRate === undefined
            ? undefined
            : Number((bucket.winRate * 100).toFixed(1)),
        avgReturn:
          bucket.avgReturnPct === null || bucket.avgReturnPct === undefined
            ? undefined
            : Number(bucket.avgReturnPct.toFixed(1)),
      })),
    [reviewData],
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (pageError || !account) {
    return (
      <div className="space-y-6">
        <Link
          to="/app/dashboard"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Link>

        <EmptyState
          title="Unable to load account"
          description={
            pageError || 'The requested account could not be loaded right now.'
          }
          action={
            <button
              onClick={loadAccountPage}
              className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </button>
          }
        />
      </div>
    );
  }

  const reviewHasAnyData =
    (reviewData?.summary.reviewedRecommendations || 0) > 0 ||
    (reviewData?.rows?.length || 0) > 0 ||
    (reviewData?.dailySuggestionCohorts?.rows.length || 0) > 0;
  const reviewTrust = getTrustAssessment(reviewData?.summary);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/app/dashboard"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">
            {account.nickname}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {account.accountNumber
              ? `Account ending in ${account.accountNumber.slice(-4)}`
              : account.accountId}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadAccountPage}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          {accountTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' ? (
        <div className="space-y-6">
          <TierPageBanner
            title="Account tier stack"
            description="Macro ETF (Tier 1) and thesis-driven options (Tier 6) run as independent logic paths with separate capital envelopes."
          />
          {tierContextForRail ? (
            <div className="rounded-xl border border-slate-200 bg-slate-950 p-4">
              <TierSpectrumRail
                context={tierContextForRail}
                filter={tierFilter}
                onFilterChange={setTierFilter}
                accountId={accountId}
              />
            </div>
          ) : null}
          {accountId && tierContextForRail ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {tierContextForRail.tiers
                .filter((t) => t.enabled || t.budget)
                .map((t) => (
                  <TierPerformanceOverTimeChart
                    key={t.riskTier}
                    accountId={accountId}
                    riskTier={t.riskTier}
                  />
                ))}
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Total Value"
              value={formatCurrency(account.balances?.totalValue)}
              helper="Current account balance"
              icon={<Wallet className="h-5 w-5" />}
            />
            <SummaryCard
              label="Cash Balance"
              value={formatCurrency(account.balances?.cashBalance)}
              helper="Available cash"
              icon={<Wallet className="h-5 w-5" />}
            />
            <SummaryCard
              label="Market Value"
              value={formatCurrency(account.balances?.marketValue)}
              helper="Invested capital"
              icon={<TrendingUp className="h-5 w-5" />}
            />
            <SummaryCard
              label="Utilization"
              value={formatPercent((account.utilizationPct || 0) * 100)}
              helper={`Max ${formatPercent((account.maxUtilization || 0) * 100)}`}
              icon={<Gauge className="h-5 w-5" />}
            />
          </div>

          {!macroTier1Slot && !optionsTier6Slot ? (
            <Card>
              <CardHeader>
                <CardTitle>Portfolio tiers</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-700">
                No Tier 1 / Tier 6 portfolio slots on this account yet. Configure them under{' '}
                <Link to="/app/settings" className="font-medium text-primary-600 hover:underline">
                  Settings → Portfolio setup
                </Link>{' '}
                (enable tiers, allocations must total 100%, then save).
              </CardContent>
            </Card>
          ) : null}

          {macroTier1Slot || optionsTier6Slot ? (
            <AccountPortfolioOverviewCard
              navUsd={account.balances?.totalValue ?? 0}
              maxUtilization={account.maxUtilization ?? 0.5}
              tier1Enabled={!!macroTier1}
              tier6Enabled={!!optionsTier6}
              tier1Alloc={macroTier1Slot?.allocationPct ?? 0}
              tier6Alloc={optionsTier6Slot?.allocationPct ?? 0}
              budgets={tierBudgets}
              tier1Id={macroTier1Slot?.id}
              tier6Id={optionsTier6Slot?.id}
              onOpenTier1={() => setActiveTab('tier1Portfolio')}
              onOpenTier6={() => setActiveTab('tier6Portfolio')}
            />
          ) : null}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <MetricRow
                  label="Broker"
                  value={account.broker || 'Unknown broker'}
                />
                <MetricRow
                  label="Account Type"
                  value={account.accountType || 'N/A'}
                />
                <MetricRow
                  label="Auto Approve"
                  value={account.autoApprove ? 'Enabled' : 'Disabled'}
                />
                <MetricRow
                  label="Auto Exit (configured)"
                  value={
                    account.autoExit == null
                      ? 'Inherits Auto Approve'
                      : account.autoExit
                        ? 'Enabled'
                        : 'Disabled'
                  }
                />
                <MetricRow
                  label="Auto Exit (effective)"
                  value={account.autoExitEffective ? 'Enabled' : 'Disabled'}
                />
                <MetricRow
                  label="Sync Enabled"
                  value={account.syncEnabled ? 'Enabled' : 'Disabled'}
                />
                <MetricRow
                  label="Last Sync"
                  value={formatDateTime(account.lastSyncAt)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Buying Power</CardTitle>
              </CardHeader>
              <CardContent>
                <MetricRow
                  label="Option Buying Power"
                  value={formatCurrency(account.balances?.optionBuyingPower)}
                />
                <MetricRow
                  label="Stock Buying Power"
                  value={formatCurrency(account.balances?.stockBuyingPower)}
                />
                <MetricRow
                  label="Max Option Position"
                  value={formatCurrency(account.maxOptionPosition)}
                />
                <MetricRow
                  label="Max Equity Position"
                  value={formatCurrency(account.maxEquityPosition)}
                />
                <MetricRow
                  label="Open Positions"
                  value={formatNumber(currentPositions.length)}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      ) : null}

      {activeTab === 'positions' ? (
        <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Positions by tier</CardTitle>
              <p className="mt-1 text-sm text-gray-500">
                Grouped by risk tier slot. Origin shows macro registry vs thesis
                orchestrator.
              </p>
            </div>
            <button
              onClick={loadPositions}
              disabled={positionsLoading}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${positionsLoading ? 'animate-spin' : ''}`}
              />
              Refresh Positions
            </button>
          </CardHeader>
          <CardContent>
            {currentPositions.length === 0 ? (
              <EmptyState
                title="No current positions"
                description="Open and pending positions for this account will appear here once orchestration creates them."
              />
            ) : (
              <div className="space-y-8">
                {[...ALL_RISK_TIER_NUMBERS, 'unassigned' as const].map((tierKey) => {
                  const tierPositions = positionsByTier[tierKey];
                  if (!tierPositions.length) return null;
                  if (
                    tierFilter !== 'all' &&
                    tierKey !== 'unassigned' &&
                    tierFilter !== tierKey
                  ) {
                    return null;
                  }
                  if (tierFilter !== 'all' && tierKey === 'unassigned') {
                    return null;
                  }
                  return (
                    <div key={String(tierKey)}>
                      <div className="mb-2 flex items-center gap-2">
                        {tierKey === 'unassigned' ? (
                          <TierChip variant="unassigned" />
                        ) : (
                          <TierChip riskTier={tierKey} />
                        )}
                        <span className="text-sm text-gray-500">
                          {tierPositions.length} position
                          {tierPositions.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Symbol
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Origin
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Direction
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Allocated Capital
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Confidence
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Updated
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {tierPositions.map((position) => {
                      const isSelected = selectedPositionId === position._id;
                      return (
                      <tr
                        key={position._id}
                        onClick={() =>
                          setSelectedPositionId(
                            isSelected ? null : position._id,
                          )
                        }
                        className={`cursor-pointer hover:bg-gray-50 ${
                          isSelected ? 'bg-primary-50' : ''
                        }`}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                          {position.symbol}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
                          {position.positionOrigin === 'macro_etf_tier1'
                            ? 'Macro ETF'
                            : position.positionOrigin === 'thesis_orchestrator'
                              ? 'Thesis orchestrator'
                              : position.macroSleeveKey
                                ? `Sleeve ${position.macroSleeveKey}`
                                : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getPositionStatusTone(
                              position.status,
                            )}`}
                          >
                            {position.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {position.tradeDirection || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {formatCurrency(position.allocatedCapital)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {formatConfidence(position.confidence)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                          {formatDateTime(position.updatedAt)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedPositionId ? (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Intraday Timeline
                    </h3>
                    <p className="text-sm text-gray-500">
                      {
                        currentPositions.find(
                          (position) => position._id === selectedPositionId,
                        )?.symbol
                      }{' '}
                      — snapshots every 15 minutes during active monitoring.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadPositionSnapshots(selectedPositionId)}
                    disabled={positionSnapshotsLoading}
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`mr-2 h-4 w-4 ${
                        positionSnapshotsLoading ? 'animate-spin' : ''
                      }`}
                    />
                    Refresh Timeline
                  </button>
                </div>
                {positionSnapshotsLoading ? (
                  <LoadingSpinner />
                ) : (
                  <PositionIntradayTimeline
                    snapshots={positionSnapshots}
                    symbol={
                      currentPositions.find(
                        (position) => position._id === selectedPositionId,
                      )?.symbol || ''
                    }
                  />
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Schwab orphans</CardTitle>
              <p className="mt-1 text-sm text-gray-500">
                Broker holdings not claimed by a Marlin position. Close places a
                MARKET flatten on Schwab only.
              </p>
            </div>
            <button
              type="button"
              onClick={loadHoldingsOrphans}
              disabled={orphansLoading}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${orphansLoading ? 'animate-spin' : ''}`}
              />
              Refresh Orphans
            </button>
          </CardHeader>
          <CardContent>
            {orphanActionMessage ? (
              <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {orphanActionMessage}
              </div>
            ) : null}
            {orphansError ? (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {orphansError}
              </div>
            ) : null}
            {orphansLoading && !holdingsOrphans ? (
              <LoadingSpinner />
            ) : !holdingsOrphans || holdingsOrphans.schwabOnly.length === 0 ? (
              <EmptyState
                title="No Schwab-only orphans"
                description="When Schwab has option/equity qty that Marlin does not track, it will list here for a one-click MARKET close."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Symbol
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Side
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Market value
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {holdingsOrphans.schwabOnly.map((orphan) => (
                      <tr key={orphan.symbol}>
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                          {orphan.symbol}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                              orphan.isShort
                                ? 'bg-red-100 text-red-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {orphan.isShort ? 'SHORT' : 'LONG'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {orphan.isShort
                            ? orphan.shortQuantity
                            : orphan.longQuantity}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {formatCurrency(orphan.marketValue)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                          <button
                            type="button"
                            onClick={() => handleCloseOrphan(orphan.symbol)}
                            disabled={closingOrphanSymbol === orphan.symbol}
                            className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            {closingOrphanSymbol === orphan.symbol
                              ? 'Closing…'
                              : 'Close'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {holdingsOrphans.capturedAt ? (
                  <p className="mt-3 text-xs text-gray-500">
                    Live diff at {formatDateTime(holdingsOrphans.capturedAt)} ·
                    Schwab legs scanned:{' '}
                    {formatNumber(holdingsOrphans.schwabPositionCount)}
                  </p>
                ) : null}
              </div>
            )}

            {holdingsOrphans && holdingsOrphans.marlinOnly.length > 0 ? (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Marlin-only (info)
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Marlin rows whose locked OCC is absent from Schwab. Use normal
                  position close if still ACTIVE; expired/CLOSING ghosts may
                  auto-clear on monitor.
                </p>
                <ul className="mt-3 space-y-1 text-sm text-gray-700">
                  {holdingsOrphans.marlinOnly.map((ghost) => (
                    <li key={`${ghost.positionId}-${ghost.lockedOcc}`}>
                      {ghost.symbol} · {ghost.lockedOcc || 'no OCC'} · qty{' '}
                      {ghost.marlinSignedQty} · {ghost.status || 'unknown'}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {holdingsOrphans && holdingsOrphans.qtyOrSide.length > 0 ? (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Qty / side drift
                </h3>
                <ul className="mt-3 space-y-1 text-sm text-gray-700">
                  {holdingsOrphans.qtyOrSide.map((drift) => (
                    <li key={`${drift.positionId}-${drift.lockedOcc}`}>
                      {drift.lockedOcc}: Marlin {drift.marlinSignedQty} vs
                      Schwab {drift.schwabSignedQty}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
        </div>
      ) : null}

      {activeTab === 'tier1Portfolio' && accountId ? (
        macroTier1 ? (
          <Tier1PortfolioPanel
            accountId={accountId}
            tierId={macroTier1.id}
            tierName={macroTier1.name}
            autoApproveEffective={
              macroTier1.autoApprove ?? account?.autoApprove ?? false
            }
          />
        ) : (
          <PortfolioTierEmptyState
            tierLabel="Long-term (Tier 1)"
            reason={macroTier1Slot ? 'disabled' : 'not_configured'}
          />
        )
      ) : null}

      {activeTab === 'tier6Portfolio' && accountId ? (
        optionsTier6 ? (
          <Tier6OptionsPanel
            accountId={accountId}
            tierId={optionsTier6.id}
            tierName={optionsTier6.name}
            maxPositions={optionsTier6.maxPositions}
          />
        ) : (
          <PortfolioTierEmptyState
            tierLabel="Active options (Tier 6)"
            reason={optionsTier6Slot ? 'disabled' : 'not_configured'}
          />
        )
      ) : null}

      {activeTab === 'performanceReview' ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Performance Review
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Operator-facing review of recommendation outcomes, thesis
                quality, and selection efficiency for this account.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => setReviewPreset(preset.key)}
                  className={`rounded-md px-3 py-2 text-sm font-medium ${
                    reviewPreset === preset.key
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
              <button
                onClick={refreshPerformanceReview}
                disabled={reviewLoading}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${reviewLoading ? 'animate-spin' : ''}`}
                />
                Refresh Review
              </button>
            </div>
          </div>

          {reviewLoading ? (
            <EmptyState
              title="Loading performance review"
              description="Fetching account-level review metrics and recent audited rows."
            />
          ) : reviewError ? (
            <EmptyState
              title="Unable to load performance review"
              description={reviewError}
              action={
                <button
                  onClick={refreshPerformanceReview}
                  className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </button>
              }
            />
          ) : !reviewData || !reviewHasAnyData ? (
            <EmptyState
              title="No reviewed opportunities yet"
              description="This tab is ready, but there is not enough reviewed account data yet to assess recommendation quality."
            />
          ) : (
            <>
              {reviewData.dailySuggestionCohorts ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Daily Suggestion Cohorts</CardTitle>
                    <p className="mt-1 text-sm text-gray-500">
                      Day-by-day generated suggestions and average forward
                      returns for the persisted hold windows. Option suggestions
                      use option contract performance when available.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <DailySuggestionCohortTable
                      cohorts={reviewData.dailySuggestionCohorts}
                    />
                  </CardContent>
                </Card>
              ) : null}

              <Card>
                <CardHeader>
                  <CardTitle>Intraday Batch Checkpoints</CardTitle>
                  <p className="mt-1 text-sm text-gray-500">
                    Same-day suggestion performance at 10:00, 12:00, 14:00, and
                    15:45 ET with per-window high/low ranges.
                  </p>
                </CardHeader>
                <CardContent>
                  {checkpointError ? (
                    <p className="text-sm text-amber-700">{checkpointError}</p>
                  ) : null}
                  {checkpointLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <SuggestionBatchCheckpointTable batches={checkpointBatches} />
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <SummaryCard
                  label="Reviewed Recommendations"
                  value={formatNumber(
                    reviewData.summary.reviewedRecommendations,
                  )}
                  helper={`${formatNumber(reviewData.summary.selectedRecommendations)} selected of ${formatNumber(
                    reviewData.summary.eligibleOpportunities,
                  )} eligible`}
                  icon={<Target className="h-5 w-5" />}
                />
                <SummaryCard
                  label="Win Rate"
                  value={formatRatioPercent(
                    reviewData.summary.recommendationWinRate,
                  )}
                  helper={`Avg return ${formatPercent(
                    reviewData.summary.recommendationAvgReturnPct,
                  )}`}
                  icon={<TrendingUp className="h-5 w-5" />}
                />
                <SummaryCard
                  label="Basket ROC"
                  value={formatPercent(
                    reviewData.summary.basketReturnOnCapitalPct,
                  )}
                  helper={`Median return ${formatPercent(
                    reviewData.summary.recommendationMedianReturnPct,
                  )}`}
                  icon={<Wallet className="h-5 w-5" />}
                />
                <SummaryCard
                  label="Thesis Accuracy"
                  value={formatRatioPercent(reviewData.summary.thesisAccuracy)}
                  helper={`Avg deviation ${formatPercent(
                    reviewData.summary.thesisAverageDeviation,
                  )}`}
                  icon={<Target className="h-5 w-5" />}
                />
                <SummaryCard
                  label="Calibration Score"
                  value={
                    reviewData.summary.confidenceCalibrationScore === null ||
                    reviewData.summary.confidenceCalibrationScore === undefined
                      ? 'N/A'
                      : reviewData.summary.confidenceCalibrationScore.toFixed(2)
                  }
                  helper="0-1 directional score; use alongside sample status, not as a hard threshold"
                  icon={<BarChart3 className="h-5 w-5" />}
                />
                <SummaryCard
                  label="Date Range"
                  value={`${formatDate(reviewData.dateRange?.from)} - ${formatDate(
                    reviewData.dateRange?.to,
                  )}`}
                  helper="Review window"
                  icon={<CalendarRange className="h-5 w-5" />}
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Suggestion Accuracy and Selection</CardTitle>
                  <p className="mt-1 text-sm text-gray-500">
                    Account-level review of generated suggestions, including
                    selected ideas and skipped eligible opportunities in the
                    same window.
                  </p>
                </CardHeader>
                <CardContent>
                  {reviewData.selectionEfficiency ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-lg border border-gray-200 p-4">
                        <p className="text-sm font-medium text-gray-500">
                          Eligible / Selected / Skipped
                        </p>
                        <p className="mt-2 text-xl font-semibold text-gray-900">
                          {formatNumber(
                            reviewData.selectionEfficiency.eligibleCount,
                          )}
                          {' / '}
                          {formatNumber(
                            reviewData.selectionEfficiency.selectedCount,
                          )}
                          {' / '}
                          {formatNumber(
                            reviewData.selectionEfficiency.skippedCount,
                          )}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          All generated opportunities in this account review
                          window.
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 p-4">
                        <p className="text-sm font-medium text-gray-500">
                          Selected vs Skipped Return
                        </p>
                        <p className="mt-2 text-xl font-semibold text-gray-900">
                          {formatPercent(
                            reviewData.selectionEfficiency.selectedAvgReturnPct,
                          )}
                          {' vs '}
                          {formatPercent(
                            reviewData.selectionEfficiency.baselineAvgReturnPct,
                          )}
                        </p>
                        <p
                          className={`mt-1 text-xs font-medium ${
                            (reviewData.selectionEfficiency.avgReturnLift ||
                              0) >= 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          Lift{' '}
                          {formatSignedPercent(
                            reviewData.selectionEfficiency.avgReturnLift,
                          )}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 p-4">
                        <p className="text-sm font-medium text-gray-500">
                          Selected vs Skipped Win Rate
                        </p>
                        <p className="mt-2 text-xl font-semibold text-gray-900">
                          {formatRatioPercent(
                            reviewData.selectionEfficiency.selectedWinRate,
                          )}
                          {' vs '}
                          {formatRatioPercent(
                            reviewData.selectionEfficiency.baselineWinRate,
                          )}
                        </p>
                        <p
                          className={`mt-1 text-xs font-medium ${
                            (reviewData.selectionEfficiency.winRateLift || 0) >=
                            0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          Lift{' '}
                          {formatSignedRatioPercent(
                            reviewData.selectionEfficiency.winRateLift,
                          )}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 p-4">
                        <p className="text-sm font-medium text-gray-500">
                          Missed Winners / Avoided Losers
                        </p>
                        <p className="mt-2 text-xl font-semibold text-gray-900">
                          {formatNumber(
                            reviewData.selectionEfficiency.missedWinnersCount,
                          )}
                          {' / '}
                          {formatNumber(
                            reviewData.selectionEfficiency.avoidedLosersCount,
                          )}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Skipped opportunities that won vs skipped
                          opportunities that lost.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <EmptyState
                      title="Suggestion selection review unavailable"
                      description="This account does not yet have enough selected and skipped reviewed suggestions for comparison."
                    />
                  )}
                </CardContent>
              </Card>

              {reviewTrust ? (
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Decision Readiness
                      </p>
                      <h3 className="mt-2 text-lg font-semibold text-gray-900">
                        {reviewTrust.headline}
                      </h3>
                      <p className="mt-2 text-sm text-gray-600">
                        {reviewTrust.rationale}
                      </p>
                    </div>
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${getTrustTone(
                        reviewTrust.label,
                      )}`}
                    >
                      {formatTrustLabel(reviewTrust.label)}
                    </span>
                  </div>
                </div>
              ) : null}

              {reviewData.diagnostics ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Data Coverage</CardTitle>
                    <p className="mt-1 text-sm text-gray-500">
                      Quick check on how much of this window has usable 5-day
                      outcomes and linked thesis review data.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <MetricRow
                      label="Suggestions with reviewed 5D outcome"
                      value={`${formatNumber(
                        reviewData.diagnostics.reviewableSuggestionCount,
                      )} of ${formatNumber(
                        reviewData.diagnostics.loadedSuggestionOutcomeCount,
                      )}`}
                    />
                    <MetricRow
                      label="Missing reviewed 5D outcome"
                      value={formatRatioPercent(
                        reviewData.diagnostics.missingReviewWindowRate,
                      )}
                    />
                    <MetricRow
                      label="Linked theses with evaluated outcome"
                      value={
                        reviewData.diagnostics.linkedThesisCount > 0
                          ? `${formatNumber(
                              reviewData.diagnostics.matchedThesisOutcomeCount,
                            )} of ${formatNumber(
                              reviewData.diagnostics.linkedThesisCount,
                            )}`
                          : 'N/A'
                      }
                    />
                    <MetricRow
                      label="Thesis outcome coverage"
                      value={formatRatioPercent(
                        reviewData.diagnostics.thesisMatchRate,
                      )}
                    />
                  </CardContent>
                </Card>
              ) : null}

              {reviewData.timeframeAgreementReview ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Timeframe Agreement Review</CardTitle>
                    <p className="mt-1 text-sm text-gray-500">
                      Quick check on whether aligned multi-timeframe suggestions
                      are outperforming mixed or conflicted ones.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                      <div className="rounded-lg border border-gray-200 p-4 xl:col-span-2">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-gray-500">
                              Verdict
                            </p>
                            <h3 className="mt-2 text-lg font-semibold text-gray-900">
                              {
                                reviewData.timeframeAgreementReview.verdict
                                  .headline
                              }
                            </h3>
                          </div>
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${getTimeframeVerdictTone(
                              reviewData.timeframeAgreementReview.verdict.label,
                            )}`}
                          >
                            {reviewData.timeframeAgreementReview.verdict.label}
                          </span>
                        </div>
                        <p className="mt-3 text-sm text-gray-600">
                          {reviewData.timeframeAgreementReview.verdict.reason}
                        </p>
                        <div className="mt-4 rounded-lg bg-gray-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Recommended Action
                          </p>
                          <p className="mt-2 text-sm text-gray-700">
                            {
                              reviewData.timeframeAgreementReview.verdict
                                .recommendedAction
                            }
                          </p>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 p-4">
                        <p className="text-sm font-medium text-gray-500">
                          Coverage
                        </p>
                        <div className="mt-3 space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Reviewed</span>
                            <span className="font-medium text-gray-900">
                              {formatNumber(
                                reviewData.timeframeAgreementReview.coverage
                                  .reviewedSuggestions,
                              )}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Classified</span>
                            <span className="font-medium text-gray-900">
                              {formatNumber(
                                reviewData.timeframeAgreementReview.coverage
                                  .classifiedSuggestions,
                              )}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Coverage</span>
                            <span className="font-medium text-gray-900">
                              {formatRatioPercent(
                                reviewData.timeframeAgreementReview.coverage
                                  .coveragePct,
                              )}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Unclassified</span>
                            <span className="font-medium text-gray-900">
                              {formatNumber(
                                reviewData.timeframeAgreementReview.coverage
                                  .unclassifiedCount,
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="mt-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getTimeframeStatusTone(
                              reviewData.timeframeAgreementReview.coverage
                                .classificationStatus === 'full'
                                ? 'ok'
                                : reviewData.timeframeAgreementReview.coverage
                                      .classificationStatus === 'partial'
                                  ? 'low_sample'
                                  : 'insufficient_data',
                            )}`}
                          >
                            {reviewData.timeframeAgreementReview.coverage.classificationStatus.replace(
                              /_/g,
                              ' ',
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div className="rounded-lg border border-gray-200 p-4">
                        <p className="text-sm font-medium text-gray-500">
                          Aligned vs Mixed
                        </p>
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs uppercase tracking-wide text-gray-500">
                              Avg Return Delta
                            </p>
                            <p className="mt-2 text-lg font-semibold text-gray-900">
                              {formatSignedPercent(
                                reviewData.timeframeAgreementReview.comparisons
                                  .alignedVsMixed.avgReturnDeltaPct,
                              )}
                            </p>
                          </div>
                          <div className="rounded-lg bg-gray-50 p-3">
                            <p className="text-xs uppercase tracking-wide text-gray-500">
                              Win Rate Delta
                            </p>
                            <p className="mt-2 text-lg font-semibold text-gray-900">
                              {formatSignedRatioPercent(
                                reviewData.timeframeAgreementReview.comparisons
                                  .alignedVsMixed.winRateDeltaPct,
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getTimeframeStatusTone(
                              reviewData.timeframeAgreementReview.comparisons
                                .alignedVsMixed.sampleStatus,
                            )}`}
                          >
                            {reviewData.timeframeAgreementReview.comparisons.alignedVsMixed.sampleStatus.replace(
                              /_/g,
                              ' ',
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="rounded-lg border border-gray-200 p-4">
                          <p className="text-sm font-medium text-gray-500">
                            Best Patterns
                          </p>
                          {reviewData.timeframeAgreementReview.patternFindings
                            .best.length === 0 ? (
                            <p className="mt-3 text-sm text-gray-500">
                              No clear positive pattern yet.
                            </p>
                          ) : (
                            <div className="mt-3 space-y-2">
                              {reviewData.timeframeAgreementReview.patternFindings.best.map(
                                (finding) => (
                                  <p
                                    key={finding}
                                    className="text-sm text-gray-700"
                                  >
                                    {finding}
                                  </p>
                                ),
                              )}
                            </div>
                          )}
                        </div>

                        <div className="rounded-lg border border-gray-200 p-4">
                          <p className="text-sm font-medium text-gray-500">
                            Worst Patterns
                          </p>
                          {reviewData.timeframeAgreementReview.patternFindings
                            .worst.length === 0 ? (
                            <p className="mt-3 text-sm text-gray-500">
                              No clear negative pattern yet.
                            </p>
                          ) : (
                            <div className="mt-3 space-y-2">
                              {reviewData.timeframeAgreementReview.patternFindings.worst.map(
                                (finding) => (
                                  <p
                                    key={finding}
                                    className="text-sm text-gray-700"
                                  >
                                    {finding}
                                  </p>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Cohort
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Samples
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Selected
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Win Rate
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Avg Return
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Avg Confidence
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {reviewData.timeframeAgreementReview.cohorts.map(
                            (cohort) => (
                              <tr key={cohort.cohort}>
                                <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900">
                                  {formatCohortLabel(cohort.cohort)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {formatNumber(cohort.sampleCount)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {formatNumber(cohort.selectedCount)}
                                  <span className="ml-1 text-gray-400">
                                    ({formatRatioPercent(cohort.selectionRate)})
                                  </span>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {formatRatioPercent(cohort.winRate)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {formatPercent(cohort.avgReturnPct)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {formatConfidence(cohort.avgConfidence)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm">
                                  <span
                                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getTimeframeStatusTone(
                                      cohort.status,
                                    )}`}
                                  >
                                    {cohort.status.replace(/_/g, ' ')}
                                  </span>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <Card className="xl:col-span-1">
                  <CardHeader>
                    <CardTitle>Recommendation Quality</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reviewData.recommendationQuality ? (
                      <>
                        <MetricRow
                          label="Reviewed Count"
                          value={formatNumber(
                            reviewData.recommendationQuality.count,
                          )}
                        />
                        <MetricRow
                          label="Winners / Losers"
                          value={`${formatNumber(reviewData.recommendationQuality.winners)} / ${formatNumber(
                            reviewData.recommendationQuality.losers,
                          )}`}
                        />
                        <MetricRow
                          label="Win Rate"
                          value={formatRatioPercent(
                            reviewData.recommendationQuality.winRate,
                          )}
                        />
                        <MetricRow
                          label="Average Return"
                          value={formatPercent(
                            reviewData.recommendationQuality.avgReturnPct,
                          )}
                        />
                        <MetricRow
                          label="Median Return"
                          value={formatPercent(
                            reviewData.recommendationQuality.medianReturnPct,
                          )}
                        />
                        <MetricRow
                          label="Hypothetical P/L"
                          value={formatCurrency(
                            reviewData.recommendationQuality
                              .totalPnlPerContract,
                          )}
                        />
                        <MetricRow
                          label="Basket ROC"
                          value={formatPercent(
                            reviewData.recommendationQuality
                              .basketReturnOnCapitalPct,
                          )}
                        />
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                          <TradeHighlight
                            title="Best Trade"
                            trade={reviewData.recommendationQuality.bestTrade}
                          />
                          <TradeHighlight
                            title="Worst Trade"
                            trade={reviewData.recommendationQuality.worstTrade}
                          />
                        </div>
                      </>
                    ) : (
                      <EmptyState
                        title="Recommendation metrics unavailable"
                        description="Recommendation outcomes have not been populated for this review window."
                      />
                    )}
                  </CardContent>
                </Card>

                <Card className="xl:col-span-1">
                  <CardHeader>
                    <CardTitle>Thesis Quality</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reviewData.thesisQuality ? (
                      <>
                        <MetricRow
                          label="Reviewed Theses"
                          value={formatNumber(reviewData.thesisQuality.count)}
                        />
                        <MetricRow
                          label="Accuracy"
                          value={formatRatioPercent(
                            reviewData.thesisQuality.accuracy,
                          )}
                        />
                        <MetricRow
                          label="Average Deviation"
                          value={formatPercent(
                            reviewData.thesisQuality.averageDeviation,
                          )}
                        />

                        <div className="mt-4 overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Direction
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Count
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Accuracy
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                  Avg Deviation
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {reviewData.thesisQuality.byDirection.map(
                                (row) => (
                                  <tr key={row.direction}>
                                    <td className="whitespace-nowrap px-3 py-2 text-sm font-medium capitalize text-gray-900">
                                      {row.direction}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                      {formatNumber(row.count)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                      {formatRatioPercent(row.accuracy)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                      {formatPercent(row.averageDeviation)}
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <EmptyState
                        title="Thesis metrics unavailable"
                        description="Recommendation outcomes are available, but thesis-level review is still being populated."
                      />
                    )}
                  </CardContent>
                </Card>

                <Card className="xl:col-span-1">
                  <CardHeader>
                    <CardTitle>Selection Efficiency</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reviewData.selectionEfficiency ? (
                      <>
                        <MetricRow
                          label="Eligible Opportunities"
                          value={formatNumber(
                            reviewData.selectionEfficiency.eligibleCount,
                          )}
                        />
                        <MetricRow
                          label="Selected Opportunities"
                          value={formatNumber(
                            reviewData.selectionEfficiency.selectedCount,
                          )}
                        />
                        <MetricRow
                          label="Skipped Baseline Status"
                          value={formatStatusLabel(
                            reviewData.selectionEfficiency.baselineStatus,
                          )}
                        />
                        <MetricRow
                          label="Selected Win Rate"
                          value={formatRatioPercent(
                            reviewData.selectionEfficiency.selectedWinRate,
                          )}
                        />
                        <MetricRow
                          label="Skipped Baseline Win Rate"
                          value={formatRatioPercent(
                            reviewData.selectionEfficiency.baselineWinRate,
                          )}
                        />
                        <MetricRow
                          label="Selected Avg Return"
                          value={formatPercent(
                            reviewData.selectionEfficiency.selectedAvgReturnPct,
                          )}
                        />
                        <MetricRow
                          label="Skipped Baseline Avg Return"
                          value={formatPercent(
                            reviewData.selectionEfficiency.baselineAvgReturnPct,
                          )}
                        />
                        <MetricRow
                          label="Win Rate Lift"
                          value={formatPercent(
                            reviewData.selectionEfficiency.winRateLift ===
                              null ||
                              reviewData.selectionEfficiency.winRateLift ===
                                undefined
                              ? null
                              : reviewData.selectionEfficiency.winRateLift *
                                  100,
                          )}
                          valueClassName={
                            (reviewData.selectionEfficiency.winRateLift || 0) >=
                            0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }
                        />
                        <MetricRow
                          label="Avg Return Lift"
                          value={formatPercent(
                            reviewData.selectionEfficiency.avgReturnLift,
                          )}
                          valueClassName={
                            (reviewData.selectionEfficiency.avgReturnLift ||
                              0) >= 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }
                        />
                        <MetricRow
                          label="Avoided Losers"
                          value={formatNumber(
                            reviewData.selectionEfficiency.avoidedLosersCount,
                          )}
                        />
                        <MetricRow
                          label="Missed Winners"
                          value={formatNumber(
                            reviewData.selectionEfficiency.missedWinnersCount,
                          )}
                        />
                        <p className="mt-4 text-xs text-gray-500">
                          Baseline metrics compare selected trades against
                          skipped opportunities from the same review window.
                          Lift stays conservative until at least three skipped
                          reviewed opportunities exist.
                        </p>
                      </>
                    ) : (
                      <EmptyState
                        title="Selection metrics unavailable"
                        description="Selected-versus-eligible comparison is not yet populated for this account window."
                      />
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Cumulative Hypothetical P/L</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {cumulativePnlData.length === 0 ? (
                      <EmptyState
                        title="No cumulative P/L trend yet"
                        description="Trend data will appear once the backend has enough reviewed buckets to graph."
                      />
                    ) : (
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={cumulativePnlData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" />
                            <YAxis
                              tickFormatter={(value) =>
                                `${value < 0 ? '-' : ''}$${Math.abs(value)}`
                              }
                            />
                            <Tooltip
                              formatter={(value: number, name: string) => [
                                formatCurrency(value),
                                name === 'selected'
                                  ? 'Selected'
                                  : 'Skipped Baseline',
                              ]}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="selected"
                              name="selected"
                              stroke="#2563eb"
                              strokeWidth={3}
                              dot={false}
                              connectNulls
                            />
                            <Line
                              type="monotone"
                              dataKey="baseline"
                              name="skipped baseline"
                              stroke="#94a3b8"
                              strokeWidth={2}
                              dot={false}
                              connectNulls
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Thesis Accuracy Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {thesisTrendData.length === 0 ? (
                      <EmptyState
                        title="No thesis trend yet"
                        description="Not enough thesis review buckets exist yet to show a trend."
                      />
                    ) : (
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={thesisTrendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" />
                            <YAxis tickFormatter={(value) => `${value}%`} />
                            <Tooltip
                              formatter={(value: number, name: string) => [
                                `${value}%`,
                                name === 'accuracy' ? 'Accuracy' : name,
                              ]}
                            />
                            <Line
                              type="monotone"
                              dataKey="accuracy"
                              stroke="#10b981"
                              strokeWidth={3}
                              dot={{ r: 3 }}
                              connectNulls
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Confidence Calibration</CardTitle>
                  <p className="mt-1 text-sm text-gray-500">
                    Compares stated confidence against realized 5-day outcomes.
                    Treat low-sample buckets as directional, not decisive.
                  </p>
                </CardHeader>
                <CardContent>
                  {confidenceBucketData.length === 0 ? (
                    <EmptyState
                      title="Not enough reviewed opportunities yet to assess calibration"
                      description="Confidence buckets will populate once more reviewed outcomes are available."
                    />
                  ) : (
                    <div className="space-y-6">
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={confidenceBucketData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" />
                            <YAxis
                              yAxisId="left"
                              tickFormatter={(value) => `${value}%`}
                            />
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              tickFormatter={(value) => `${value}`}
                            />
                            <Tooltip
                              formatter={(value: number, name: string) => [
                                name === 'sampleCount'
                                  ? formatNumber(value)
                                  : `${value}${name === 'avgReturn' ? '%' : '%'}`,
                                name === 'winRate'
                                  ? 'Win Rate'
                                  : name === 'avgReturn'
                                    ? 'Avg Return'
                                    : 'Sample Count',
                              ]}
                            />
                            <Legend />
                            <Bar
                              yAxisId="left"
                              dataKey="winRate"
                              fill="#2563eb"
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar
                              yAxisId="right"
                              dataKey="sampleCount"
                              fill="#cbd5e1"
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Bucket
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Samples
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Selected
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Win Rate
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Avg Return
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {confidenceBucketData.map((bucket) => (
                              <tr key={bucket.label}>
                                <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900">
                                  {bucket.label}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {formatNumber(bucket.sampleCount)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {formatNumber(bucket.selectedCount)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {bucket.winRate === undefined
                                    ? 'N/A'
                                    : `${bucket.winRate}%`}
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                  {bucket.avgReturn === undefined
                                    ? 'N/A'
                                    : `${bucket.avgReturn}%`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Reviewed Outcomes</CardTitle>
                </CardHeader>
                <CardContent>
                  {!reviewData.rows || reviewData.rows.length === 0 ? (
                    <EmptyState
                      title="No reviewed rows returned"
                      description="Summary metrics are available, but the row-level review table is still empty for this account window."
                    />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Date
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Symbol
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Trade Direction
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Thesis Direction
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Confidence
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Selected
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Close Return
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Thesis Correct
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Notes
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {reviewData.rows.map((row) => (
                            <tr
                              key={`${row.symbol}-${row.date}-${row.generatedBucket || 'review'}`}
                            >
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                {formatDate(row.date)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900">
                                {row.symbol}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                {row.tradeDirection || 'N/A'}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                {row.thesisDirection || 'N/A'}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                {formatConfidence(row.confidence)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm">
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                                    row.selected
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {row.selected ? 'Selected' : 'Eligible'}
                                </span>
                              </td>
                              <td
                                className={`whitespace-nowrap px-3 py-2 text-sm ${
                                  (row.closeReturnPct ??
                                    row.liveReturnPct ??
                                    0) >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}
                              >
                                {formatPercent(
                                  row.closeReturnPct ?? row.liveReturnPct,
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                {row.thesisCorrect === null ||
                                row.thesisCorrect === undefined
                                  ? 'N/A'
                                  : row.thesisCorrect
                                    ? 'Yes'
                                    : 'No'}
                              </td>
                              <td className="max-w-md px-3 py-2 text-sm text-gray-700">
                                <div className="line-clamp-2">
                                  {row.note || '-'}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AccountDetail;
